// 일일 콘텐츠 장부(D3c, PRD 5·2.4): 일일 의뢰 3 · 일일 던전 2종(1일 2회) · 무한의 탑 1~50(1일 1 이어하기).
// 순수·결정적이다. 시간은 항상 인자(nowMs)로 받는다 — 실시계를 읽지 않는다. 날짜 경계는 KST 00:00(loot.ts 주 경계와 같은 시간대).
// 기기 시계 조작 방지: 본 가장 늦은 시각(seenMs)보다 과거 시각은 seenMs로 보정해 날짜가 거꾸로 가지 않는다.
// (서버 시간 권위는 서버 저장 이전 때 같은 reducer를 서버가 돈다 — PRD 2.4)
import { PROTECT_SUPPLY, ROUTINE, regionGoldMult, stoneTierForIlvl, type StoneTier } from './content/economy';
import { LIVE_WEEKS, dungeonPerDay, liveGoalKey, liveWeekIndex, liveWeekOf, type LiveGoal, type LiveGoalType } from './content/liveops';
import { DAILY_QUESTS } from './content/quests';
import { TOWER_FLOOR_MAX, towerHighReward } from './content/towerHigh';
import { townGearLevel } from './content/townShop';
import type { QuestDef, RegionNo } from './content/types';

/** v2(L9): 지급 장부(owed) 추가. v1 저장본의 누적 보상은 한 번도 지갑에 들어가지 않았으므로 읽을 때 한 건(dl:legacy-v1)으로 옮긴다. v3(C5): 주간 이벤트 장부(weekly) 추가 — v2 저장본은 빈 주간 장부로 읽는다 */
export const DAILY_VERSION = 3;
export const DAY_MS = 24 * 3600 * 1000;
/** KST = UTC+9. 하루는 KST 00:00에 바뀐다 */
export const KST_OFFSET_MS = 9 * 3600 * 1000;
/** 같은 연산 id 중복 방지로 기억하는 수 */
export const DAILY_APPLIED_CAP = 100;

/**
 * 지갑 지급 한 건(L9 지급 계약). 일일 장부는 "줄 것"만 적고, 실제 지급은 게임이 고정 id로 다른 장부에 넣는다:
 *  - 골드  → save.v1 보너스 영수증 id = payout.id (영수증은 지워지지 않아 같은 id는 영원히 한 번)
 *  - 강화석 → 장비 장부 'stones' 연산 id = payout.id + ':s' · 보호 주문서 → 'protect' 연산 id = payout.id + ':p' (dl: 접두는 장비 장부가 오래 기억한다)
 * 두 장부 저장이 확인된 뒤에만 paid = true. 그 전에는 몇 번을 다시 넣어도(두 탭·새로고침·재접속) 받는 쪽이 같은 id를 거른다.
 * 젬(유료 재화)은 서버 장부만 지급할 수 있어 여기 넣지 않는다 — granted.gems에 '보관'으로 남는다(서버 검증 탑 기록 이후 지급).
 */
export interface Payout { id: string; at: number; gold: number; stones: number; tier: StoneTier; protect: number; town: number; paid: boolean }
/** 지급이 끝난 건을 기억하는 수(지급 안 된 건은 수와 상관없이 남는다) */
export const PAYOUT_PAID_CAP = 64;
/** 연산 id → 지급 id */
export const payoutId = (opId: string): string => 'dl:' + opId;
/** v1에서 옮긴 미지급 누적분 지급 id(두 탭이 동시에 옮겨도 한 번) */
export const LEGACY_PAYOUT_ID = 'dl:legacy-v1';
/** 탑 층 첫 클리어 지급 id(층마다 고정) */
export const towerPayoutId = (floor: number): string => 'dl:tower-f' + floor;
/** 이 마을에서 받는 강화석 등급(마을 장비 레벨 기준 — 상점·드롭과 같은 등급) */
export function stoneTierForTown(town: number): StoneTier { return stoneTierForIlvl(townGearLevel(Math.max(1, Math.min(6, town)))); }

/** 연산 영수증: 같은 id 중복 방지 + 두 탭을 합칠 때 한쪽에만 있는 연산을 다시 재생한다 */
export interface LogEntry { op: DailyOp; at: number; town: number; region6: boolean }

export function dayIndex(nowMs: number): number { return Math.floor((nowMs + KST_OFFSET_MS) / DAY_MS); }
/** 다음 날짜 경계(KST 00:00)의 UTC ms */
export function nextResetMs(nowMs: number): number { return (dayIndex(nowMs) + 1) * DAY_MS - KST_OFFSET_MS; }

export type DungeonKind = 'vault' | 'mine';
export const DUNGEON_KINDS: readonly DungeonKind[] = ['vault', 'mine'];
export const DUNGEON_INFO: Readonly<Record<DungeonKind, { readonly name: string; readonly perDay: number; readonly town: number }>> = {
  vault: { name: '황금 보물고', perDay: ROUTINE.goldVault.perDay, town: ROUTINE.goldVault.town },
  mine: { name: '강화석 광산', perDay: ROUTINE.stoneMine.perDay, town: ROUTINE.stoneMine.town },
};

/** 탑: 1~50층(Day-0) + 51~100층(C5, content/towerHigh.ts), 10층마다 보스. 보상은 층을 처음 깰 때만 */
export const TOWER_MAX = TOWER_FLOOR_MAX;
export const TOWER_UNLOCK_TOWN = 3;
export function isBossFloor(f: number): boolean { return f > 0 && f % 10 === 0; }
export interface Reward { gold: number; stones: number; gems: number; protect: number }
const ZERO = (): Reward => ({ gold: 0, stones: 0, gems: 0, protect: 0 });
/** 층 첫 클리어 보상: 보스 층 젬 소량, 30·50층 보호 주문서 1(items.PROTECT_WEEKLY.tower 주 2장 안), 나머지는 골드 소량 */
/** goldMult = 이번 주 이벤트의 탑 골드 배율(liveops mods.towerGold, 없으면 1) */
export function towerReward(floor: number, region6 = false, goldMult = 1): Reward {
  const r = ZERO();
  if (floor < 1 || floor > TOWER_MAX) return r;
  if (floor > 50) { Object.assign(r, towerHighReward(floor, region6)); r.gold = Math.round(r.gold * goldMult); return r; }
  r.gold = Math.round((200 + floor * 40) * goldMult);
  if (isBossFloor(floor)) r.gems = 5 + floor / 10 * 2;
  // 보호 주문서는 지역6 클리어 뒤부터(economy PROTECT_SUPPLY). 그 전 칸은 강화석으로 준다. 주간 반복 공급(PROTECT_WEEKLY.tower)은 1~50 이후 탑 확장 때 별도로
  if (floor === 30 || floor === 50) { if (region6) r.protect = 1; else r.stones = PROTECT_SUPPLY.preUnlockStones; }
  return r;
}

export interface QuestRow { progress: number; claimed: boolean }
/** 주간 이벤트 장부(C5): week = liveWeekIndex(이 장부의 날짜), goals = liveGoalKey → 진행·수령, finale = 마무리 보상 수령 */
export interface WeeklyState {
  week: number; goals: Record<string, QuestRow>; finale: boolean;
  /** 이번 주 이벤트 진행에 이미 센 현상금 연산 id. 연산 영수증(log)은 상한 때문에 밀려나므로 같은 날 현상금 id를 다시 제출해도(패널이 새로고침마다 오늘 완료분을 다시 보낸다) 진행이 두 번 오르지 않게 이 주 동안 따로 기억한다 */
  bounty: string[];
}
/** 한 주에 셀 수 있는 현상금 연산 id 수(하루 2마리 × 7일 = 14, 여유 포함) */
export const WEEKLY_BOUNTY_SEEN_CAP = 64;
const freshWeekly = (day: number): WeeklyState => ({ week: liveWeekIndex(day), goals: {}, finale: false, bounty: [] });
export interface DailyState {
  v: number;
  /** 쓰기마다 1 증가(탭 합치기: 더 큰 쪽) */
  rev: number;
  /** 이 장부의 날짜(KST 일 번호). 바뀌면 일일 칸이 새로 시작한다 */
  day: number;
  /** 본 가장 늦은 시각(시계 되돌리기 방어) */
  seenMs: number;
  quests: Record<string, QuestRow>;
  /** 이번 이벤트 주 장부(월요일 KST 00:00에 새로 시작) */
  weekly: WeeklyState;
  /** 오늘 던전 사용 횟수 */
  dungeon: Record<DungeonKind, number>;
  /** 들어가 있는 던전(결과 대기). 없으면 null */
  inRun: DungeonKind | null;
  tower: {
    /** 지금까지 깬 가장 높은 층(영구) */
    best: number;
    /** 오늘 이어하기 시작 여부 */
    startedDay: number;
    /** 도전 중(towerStart ~ towerEnd). 끝난 뒤 늦게 온 towerClear를 막는다 */
    active: boolean;
    /** 이번 도전에서 올라가 있는 층(0 = 도전 중 아님). 다음 층 = floor + 1 */
    floor: number;
  };
  /** 지금까지 받은 보상 합(정산 장부: 골드·강화석 등 실제 지급은 이 증분을 소비하는 쪽이 한다) */
  granted: Reward;
  /** 최근 적용한 연산 영수증(오래된 것부터 버림). 중복 판정과 탭 합치기 재생의 근거 */
  log: LogEntry[];
  /** 지갑 지급 장부(Payout). 지급 안 된 건은 모두, 지급된 건은 최근 PAYOUT_PAID_CAP개 */
  owed: Payout[];
}

export function freshDaily(nowMs: number): DailyState {
  return {
    v: DAILY_VERSION, rev: 0, day: dayIndex(nowMs), seenMs: nowMs, quests: {}, weekly: freshWeekly(dayIndex(nowMs)), dungeon: { vault: 0, mine: 0 }, inRun: null,
    tower: { best: 0, startedDay: -1, active: false, floor: 0 }, granted: ZERO(), log: [], owed: [],
  };
}

export type DailyOp =
  | { readonly id: string; readonly t: 'questProgress'; readonly goal: 'kill' | 'enhance' | 'bounty'; readonly n: number }
  | { readonly id: string; readonly t: 'questClaim'; readonly quest: string }
  | { readonly id: string; readonly t: 'dungeonEnter'; readonly kind: DungeonKind }
  | { readonly id: string; readonly t: 'dungeonDone'; readonly kind: DungeonKind; readonly success: boolean }
  | { readonly id: string; readonly t: 'towerStart' }
  | { readonly id: string; readonly t: 'towerClear'; readonly floor: number }
  | { readonly id: string; readonly t: 'towerEnd' }
  /** 이번 주 이벤트 목표 수령(goal = liveGoalKey) */
  | { readonly id: string; readonly t: 'liveClaim'; readonly goal: string }
  /** 이번 주 이벤트 목표를 모두 수령한 뒤 마무리 보상 */
  | { readonly id: string; readonly t: 'liveFinale' };

export type DailyCode =
  | 'ok' | 'read_only' | 'save_failed' | 'duplicate' | 'locked' | 'not_ready' | 'already_claimed' | 'no_attempts' | 'in_run' | 'not_in_run' | 'unknown' | 'bad_floor' | 'tower_done' | 'tower_idle' | 'no_event';
export interface DailyResult { state: DailyState; code: DailyCode; reward?: Reward }

function cloneState(s: DailyState): DailyState {
  const quests: Record<string, QuestRow> = {};
  for (const k of Object.keys(s.quests)) quests[k] = { ...s.quests[k] };
  const weekly: WeeklyState = { week: s.weekly.week, goals: {}, finale: s.weekly.finale, bounty: s.weekly.bounty.slice() };
  for (const k of Object.keys(s.weekly.goals)) weekly.goals[k] = { ...s.weekly.goals[k] };
  return { ...s, quests, weekly, dungeon: { ...s.dungeon }, tower: { ...s.tower }, granted: { ...s.granted }, log: s.log.slice(), owed: s.owed.map((p) => ({ ...p })) };
}

/** 시각을 반영해 날짜 경계를 넘기고, 시계가 거꾸로 가면 본 최대 시각으로 보정한다. 입력 상태는 바꾸지 않는다 */
export function rollDaily(s: DailyState, nowMs: number): DailyState {
  const t = Math.max(nowMs, s.seenMs);
  const day = dayIndex(t);
  if (day === s.day && t === s.seenMs) return s;
  const n = cloneState(s);
  n.seenMs = t;
  if (day > s.day) {
    n.day = day;
    n.quests = {};
    if (liveWeekIndex(day) !== n.weekly.week) n.weekly = freshWeekly(day);
    n.dungeon = { vault: 0, mine: 0 };
    // 도전 중이던 던전은 날이 바뀌면 소멸(횟수는 이미 썼다). 탑 도전 층은 새 날 새 이어하기로 이어진다
    n.inRun = null;
    n.tower.floor = 0;
    n.tower.active = false;
    // 연산 영수증은 날이 바뀌어도 남긴다: 자정 전에 센 결과를 자정 뒤에 복원해도 새 날에 중복으로 걸러진다
  }
  return n;
}

/** 오늘 의뢰 목록(마을①부터). 목표 수는 DAILY_QUESTS 정본 */
export function dailyQuestGoal(q: QuestDef): { type: 'kill' | 'enhance' | 'bounty'; count: number } {
  const g = q.goals[0];
  if (g.type === 'kill' || g.type === 'enhance' || g.type === 'bounty') return { type: g.type, count: g.count };
  return { type: 'kill', count: 1 };
}

/** 일일 의뢰 골드 = 지역1 기준 값 × 지역 골드 배율(QuestReward 계약). 지역 = 도착한 가장 높은 마을 */
export function questGold(q: QuestDef, town: number): number {
  return Math.round((q.rewards.gold ?? 0) * regionGoldMult(Math.min(6, Math.max(1, town)) as RegionNo));
}

/** 지급 끝난 오래된 건만 버린다(순서 유지) */
function trimOwed(o: Payout[]): void {
  let paid = 0;
  for (const p of o) if (p.paid) paid++;
  let drop = paid - PAYOUT_PAID_CAP;
  if (drop <= 0) return;
  let w = 0;
  for (let i = 0; i < o.length; i++) { if (o[i].paid && drop > 0) { drop--; continue; } o[w++] = o[i]; }
  o.length = w;
}

/** 합친 장부 n이 src의 미지급 건 p를 인정하는가(mergeDaily) */
function owedAccepted(n: DailyState, src: DailyState, p: Payout): boolean {
  if (p.id === LEGACY_PAYOUT_ID) return true;
  if (p.id.startsWith('dl:tower-f')) return n.tower.best >= Number(p.id.slice(10));
  const opId = p.id.slice(3);
  const e = src.log.find((x) => x.op.id === opId);
  // 영수증이 잘렸거나 지난 날 연산(합치기가 재생하지 않는다 — 거절된 것이 아니다)이면 남긴다(PR #273 Codex 2차 P2)
  if (!e || dayIndex(e.at) !== n.day) return true;
  return n.log.some((x) => x.op.id === opId);
}

/** 진행 중 던전(inRun)에 들어간 시각(그 입장 영수증). 영수증이 없으면 null */
export function runEnteredAt(s: DailyState): number | null {
  if (!s.inRun) return null;
  for (let i = s.log.length - 1; i >= 0; i--) { const e = s.log[i]; if (e.op.t === 'dungeonEnter' && e.op.kind === s.inRun) return e.at; }
  return null;
}
/** 이보다 오래된 진행 중 던전은 끊긴 판으로 본다(보물고 60초·광산 3웨이브보다 넉넉히). 그 전에는 다른 탭이 하고 있을 수 있다 */
export const RUN_STALE_MS = 10 * 60 * 1000;

/** 아직 지갑에 들어가지 않은 지급 건 */
export function unpaid(s: DailyState): Payout[] { return s.owed.filter((p) => !p.paid); }

/** 지급 확인 표시(게임이 두 장부 저장을 확인한 뒤). 없는 id·이미 지급이면 그대로 돌려준다 */
export function markPaid(base: DailyState, id: string): DailyState {
  const i = base.owed.findIndex((p) => p.id === id);
  if (i < 0 || base.owed[i].paid) return base;
  const s = cloneState(base);
  s.owed[i].paid = true;
  s.rev += 1;
  trimOwed(s.owed);
  return s;
}

function addReward(into: Reward, r: Reward): void { into.gold += r.gold; into.stones += r.stones; into.gems += r.gems; into.protect += r.protect; }

/** 목표를 채웠는가. 탑을 끝까지 정복(100층)한 이용자는 더 오를 층이 없으므로 탑 목표(탑·보스 층)를 채운 것으로 본다 */
export function liveGoalReady(s: DailyState, goal: LiveGoal, row: QuestRow | undefined): boolean {
  if (row && row.progress >= goal.count) return true;
  return (goal.type === 'tower' || goal.type === 'towerBoss') && s.tower.best >= TOWER_MAX;
}

/** 이번 주 이벤트 목표 진행(종류가 같은 목표 전부에 n을 더한다) */
function liveProgress(s: DailyState, type: LiveGoalType, n: number): void {
  const w = liveWeekOf(s.day);
  if (!w || s.weekly.week !== liveWeekIndex(s.day)) return;
  for (const goal of w.goals) {
    if (goal.type !== type) continue;
    const key = liveGoalKey(w, goal);
    const row = s.weekly.goals[key] ?? { progress: 0, claimed: false };
    row.progress = Math.min(goal.count, row.progress + n);
    s.weekly.goals[key] = row;
  }
}

/** 연산 한 건 적용. town = 도착한 가장 높은 마을(해금 판단). 같은 id는 두 번 적용되지 않는다 */
export function applyDaily(base: DailyState, op: DailyOp, nowMs: number, town: number, region6 = false): DailyResult {
  const s = cloneState(rollDaily(base, nowMs));
  const fail = (code: DailyCode): DailyResult => ({ state: s, code });
  if (s.log.some((e) => e.op.id === op.id)) return fail('duplicate');
  const commit = (reward?: Reward): DailyResult => {
    s.rev += 1;
    if (reward && (reward.gold > 0 || reward.stones > 0 || reward.protect > 0)) {
      // 탑 첫 클리어는 층마다 평생 한 번이라 지급 id를 층으로 고정한다: 두 탭이 같은 층을 다른 연산 id로 깨도 받는 쪽이 한 번만 넣는다(PR #273 Codex P1)
      s.owed.push({ id: op.t === 'towerClear' ? towerPayoutId(op.floor) : payoutId(op.id), at: nowMs, gold: reward.gold, stones: reward.stones, tier: stoneTierForTown(town), protect: reward.protect, town, paid: false });
      trimOwed(s.owed);
    }
    s.log.push({ op, at: nowMs, town, region6 });
    if (s.log.length > DAILY_APPLIED_CAP) s.log.splice(0, s.log.length - DAILY_APPLIED_CAP);
    return { state: s, code: 'ok', reward };
  };
  switch (op.t) {
    case 'questProgress': {
      const n = Math.max(0, Math.floor(op.n));
      if (n === 0) return fail('unknown');
      for (const q of DAILY_QUESTS) {
        const g = dailyQuestGoal(q);
        if (g.type !== op.goal || town < q.town) continue;
        const row = s.quests[q.id] ?? { progress: 0, claimed: false };
        row.progress = Math.min(g.count, row.progress + n);
        s.quests[q.id] = row;
      }
      if (op.goal === 'bounty' && liveWeekOf(s.day) && !s.weekly.bounty.includes(op.id)) {
        s.weekly.bounty.push(op.id);
        if (s.weekly.bounty.length > WEEKLY_BOUNTY_SEEN_CAP) s.weekly.bounty.shift();
        liveProgress(s, 'bounty', n);
      }
      return commit();
    }
    case 'questClaim': {
      const q = DAILY_QUESTS.find((d) => d.id === op.quest);
      if (!q) return fail('unknown');
      if (town < q.town) return fail('locked');
      const row = s.quests[q.id];
      if (row && row.claimed) return fail('already_claimed');
      if (!row || row.progress < dailyQuestGoal(q).count) return fail('not_ready');
      row.claimed = true;
      const r = ZERO();
      r.gold = questGold(q, town);
      r.stones = q.rewards.stones ?? 0;
      addReward(s.granted, r);
      return commit(r);
    }
    case 'dungeonEnter': {
      const info = DUNGEON_INFO[op.kind];
      if (!info) return fail('unknown');
      if (town < info.town) return fail('locked');
      if (s.inRun) return fail('in_run');
      if (s.dungeon[op.kind] >= dungeonPerDay(op.kind, s.day)) return fail('no_attempts');
      s.dungeon[op.kind] += 1;
      s.inRun = op.kind;
      return commit();
    }
    case 'dungeonDone': {
      if (s.inRun !== op.kind) return fail('not_in_run');
      s.inRun = null;
      const r = ZERO();
      if (op.success) {
        // 보물고 골드도 지역 골드 배율을 곱한다(ROUTINE 계약)
        const mods = liveWeekOf(s.day)?.mods;
        if (op.kind === 'vault') r.gold = Math.round(ROUTINE.goldVault.gold * regionGoldMult(Math.min(6, Math.max(1, town)) as RegionNo) * (mods ? mods.vaultGold : 1));
        else r.stones = Math.round(ROUTINE.stoneMine.stones * (mods ? mods.mineStones : 1));
        addReward(s.granted, r);
        liveProgress(s, op.kind, 1);
      }
      return commit(r);
    }
    case 'towerStart': {
      if (town < TOWER_UNLOCK_TOWN) return fail('locked');
      if (s.tower.best >= TOWER_MAX) return fail('tower_done');
      if (s.tower.startedDay === s.day) return fail('no_attempts');
      s.tower.startedDay = s.day;
      s.tower.active = true;
      s.tower.floor = s.tower.best; // 이어하기: 다음 층 = 최고 층 + 1
      return commit();
    }
    case 'towerClear': {
      if (s.tower.startedDay !== s.day || !s.tower.active) return fail('tower_idle');
      if (op.floor !== s.tower.floor + 1 || op.floor > TOWER_MAX) return fail('bad_floor');
      s.tower.floor = op.floor;
      const r = ZERO();
      const first = op.floor > s.tower.best;
      if (first) { s.tower.best = op.floor; addReward(r, towerReward(op.floor, region6, liveWeekOf(s.day)?.mods.towerGold ?? 1)); addReward(s.granted, r); }
      liveProgress(s, 'tower', 1);
      if (isBossFloor(op.floor)) liveProgress(s, 'towerBoss', 1);
      return commit(r);
    }
    case 'towerEnd': {
      if (s.tower.startedDay !== s.day || !s.tower.active) return fail('tower_idle');
      s.tower.active = false;
      s.tower.floor = 0; // 패배·포기: 오늘 도전은 끝(내일 best+1부터)
      return commit();
    }
    case 'liveClaim': {
      const w = liveWeekOf(s.day);
      if (!w || s.weekly.week !== liveWeekIndex(s.day)) return fail('no_event');
      const goal = w.goals.find((x) => liveGoalKey(w, x) === op.goal);
      if (!goal) return fail('unknown');
      const row = s.weekly.goals[op.goal];
      if (row && row.claimed) return fail('already_claimed');
      if (!liveGoalReady(s, goal, row)) return fail('not_ready');
      s.weekly.goals[op.goal] = { progress: goal.count, claimed: true };
      const r = ZERO();
      r.gold = Math.round(goal.gold * regionGoldMult(Math.min(6, Math.max(1, town)) as RegionNo));
      r.stones = goal.stones;
      addReward(s.granted, r);
      return commit(r);
    }
    case 'liveFinale': {
      const w = liveWeekOf(s.day);
      if (!w || s.weekly.week !== liveWeekIndex(s.day)) return fail('no_event');
      if (s.weekly.finale) return fail('already_claimed');
      if (!w.goals.every((x) => s.weekly.goals[liveGoalKey(w, x)]?.claimed)) return fail('not_ready');
      s.weekly.finale = true;
      const r = ZERO();
      r.gold = Math.round(w.finale.gold * regionGoldMult(Math.min(6, Math.max(1, town)) as RegionNo));
      r.stones = w.finale.stones;
      addReward(s.granted, r);
      return commit(r);
    }
  }
}

/** 다른 탭·저장본 합치기: rev가 큰 쪽이 새 상태이고, 영구값(탑 best·granted)은 큰 쪽을 지킨다 */
export function mergeDaily(a0: DailyState, b0: DailyState): DailyState {
  // 합치기 전에 두 쪽을 같은 시각(본 최대)으로 굴린다: 어제 상태의 rev가 더 커도 오늘 연산이 걸러지지 않게
  const t = Math.max(a0.seenMs, b0.seenMs);
  const a = rollDaily(a0, t), b = rollDaily(b0, t);
  const [hi, lo] = a.rev >= b.rev ? [a, b] : [b, a];
  let n = cloneState(hi);
  n.seenMs = Math.max(a.seenMs, b.seenMs);
  // 한쪽에만 있는 오늘의 연산을 재생한다: 서로 다른 의뢰 수령·처치 입력이 둘 다 반영된다(같은 id는 reducer가 거른다)
  for (const e of lo.log) {
    if (n.log.some((x) => x.op.id === e.op.id) || dayIndex(e.at) !== n.day) continue;
    const r = applyDaily(n, e.op, e.at, e.town, e.region6);
    if (r.code === 'ok') n = r.state;
  }
  n.tower.best = Math.max(n.tower.best, a.tower.best, b.tower.best);
  // 오늘 도전을 쓴 쪽을 지킨다(재생으로도 복원되지 않는 경우의 안전판)
  const sd = Math.max(a.tower.startedDay, b.tower.startedDay);
  if (sd > n.tower.startedDay) n.tower = { ...(lo.tower.startedDay === sd ? lo.tower : hi.tower), best: n.tower.best };
  else if (a.tower.startedDay === b.tower.startedDay) n.tower.active = n.tower.active && a.tower.active && b.tower.active;
  if (lo.day === hi.day) {
    for (const k of Object.keys(lo.quests)) {
      const x = n.quests[k], y = lo.quests[k];
      n.quests[k] = x ? { progress: Math.max(x.progress, y.progress), claimed: x.claimed || y.claimed } : { ...y };
    }
    for (const k of DUNGEON_KINDS) n.dungeon[k] = Math.max(n.dungeon[k], lo.dungeon[k]);
  }
  // 이벤트 주 장부: 같은 주면 진행은 큰 쪽·수령은 한쪽이라도 받았으면 받음(날짜가 달라도 같은 주면 합친다)
  if (lo.weekly.week === n.weekly.week) {
    for (const k of Object.keys(lo.weekly.goals)) {
      const x = n.weekly.goals[k], y = lo.weekly.goals[k];
      n.weekly.goals[k] = x ? { progress: Math.max(x.progress, y.progress), claimed: x.claimed || y.claimed } : { ...y };
    }
    n.weekly.finale = n.weekly.finale || lo.weekly.finale;
    for (const b of lo.weekly.bounty) if (!n.weekly.bounty.includes(b)) n.weekly.bounty.push(b);
    if (n.weekly.bounty.length > WEEKLY_BOUNTY_SEEN_CAP) n.weekly.bounty.splice(0, n.weekly.bounty.length - WEEKLY_BOUNTY_SEEN_CAP);
  }
  // 지급 장부: 두 쪽 합집합, 지급 표시는 한쪽이라도 지급했으면 지급(받는 쪽이 같은 id를 거르므로 늦게 온 미지급 표시가 이중 지급을 만들지 않는다)
  for (const src of [a, b]) for (const p of src.owed) {
    const x = n.owed.find((q) => q.id === p.id);
    if (x) { if (p.paid) x.paid = true; continue; }
    // 미지급 건은 그 연산이 합친 장부에서도 받아들여졌을 때만 가져온다: 재생에서 거절된 연산(같은 층을 다른 탭이 먼저 깸·횟수 초과)의 몫이 지급되지 않게.
    // 연산 영수증이 이미 잘려 판정할 수 없는 건(오래된 미지급)과 v1 이관분은 그대로 둔다(잃지 않는 쪽)
    if (!p.paid && !owedAccepted(n, src, p)) continue;
    n.owed.push({ ...p });
  }
  n.owed.sort((x, y) => x.at - y.at || (x.id < y.id ? -1 : x.id > y.id ? 1 : 0));
  trimOwed(n.owed);
  // 누적 보상은 줄지 않게(재생이 못 닿는 지난 날 연산 몫은 큰 쪽으로)
  n.granted = { gold: Math.max(n.granted.gold, a.granted.gold, b.granted.gold), stones: Math.max(n.granted.stones, a.granted.stones, b.granted.stones), gems: Math.max(n.granted.gems, a.granted.gems, b.granted.gems), protect: Math.max(n.granted.protect, a.granted.protect, b.granted.protect) };
  return n;
}

/** 영수증 한 건 검증(모르는 연산·모양이 어긋난 것은 버린다) */
function parseEntry(raw: unknown): LogEntry | null {
  if (!raw || typeof raw !== 'object') return null;
  const e = raw as Record<string, unknown>;
  const o = e.op as Record<string, unknown> | undefined;
  if (!o || typeof o !== 'object' || typeof o.id !== 'string' || !o.id || typeof e.at !== 'number' || !Number.isFinite(e.at) || typeof e.town !== 'number') return null;
  let op: DailyOp | null = null;
  switch (o.t) {
    case 'questProgress': if ((o.goal === 'kill' || o.goal === 'enhance' || o.goal === 'bounty') && typeof o.n === 'number') op = { id: o.id, t: 'questProgress', goal: o.goal, n: o.n }; break;
    case 'questClaim': if (typeof o.quest === 'string') op = { id: o.id, t: 'questClaim', quest: o.quest }; break;
    case 'dungeonEnter': if (o.kind === 'vault' || o.kind === 'mine') op = { id: o.id, t: 'dungeonEnter', kind: o.kind }; break;
    case 'dungeonDone': if ((o.kind === 'vault' || o.kind === 'mine') && typeof o.success === 'boolean') op = { id: o.id, t: 'dungeonDone', kind: o.kind, success: o.success }; break;
    case 'towerStart': op = { id: o.id, t: 'towerStart' }; break;
    case 'towerClear': if (typeof o.floor === 'number') op = { id: o.id, t: 'towerClear', floor: o.floor }; break;
    case 'towerEnd': op = { id: o.id, t: 'towerEnd' }; break;
    case 'liveClaim': if (typeof o.goal === 'string' && o.goal.length <= 40) op = { id: o.id, t: 'liveClaim', goal: o.goal }; break;
    case 'liveFinale': op = { id: o.id, t: 'liveFinale' }; break;
  }
  return op ? { op, at: e.at, town: e.town, region6: e.region6 === true } : null;
}

function parsePayout(raw: unknown): Payout | null {
  if (!raw || typeof raw !== 'object') return null;
  const p = raw as Record<string, unknown>;
  if (typeof p.id !== 'string' || !p.id.startsWith('dl:') || p.id.length > 120 || typeof p.at !== 'number' || !Number.isFinite(p.at)) return null;
  const tier = intIn(p.tier, 0, 3, 0) as StoneTier;
  return { id: p.id, at: p.at, gold: intIn(p.gold, 0, 1e9, 0), stones: intIn(p.stones, 0, 1e6, 0), tier, protect: intIn(p.protect, 0, 9, 0), town: intIn(p.town, 1, 6, 1), paid: p.paid === true };
}

const intIn = (v: unknown, lo: number, hi: number, d: number): number => (typeof v === 'number' && Number.isFinite(v) ? Math.min(hi, Math.max(lo, Math.floor(v))) : d);

export function serializeDaily(s: DailyState): string { return JSON.stringify(s); }
/** 저장 원문을 검증해 읽는다. 읽을 수 없으면 null(호출자가 손상 처리) */
export function parseDaily(raw: string, nowMs: number): DailyState | null {
  let o: unknown;
  try { o = JSON.parse(raw); } catch { return null; }
  if (!o || typeof o !== 'object' || Array.isArray(o)) return null;
  const r = o as Record<string, unknown>;
  if (typeof r.v !== 'number' || r.v > DAILY_VERSION) return null;
  const f = freshDaily(nowMs);
  const s: DailyState = { ...f };
  s.rev = intIn(r.rev, 0, Number.MAX_SAFE_INTEGER, 0);
  s.seenMs = intIn(r.seenMs, 0, Number.MAX_SAFE_INTEGER, nowMs);
  s.day = intIn(r.day, 0, 1e7, dayIndex(s.seenMs));
  const q = r.quests;
  if (q && typeof q === 'object') {
    for (const d of DAILY_QUESTS) {
      const row = (q as Record<string, unknown>)[d.id] as Record<string, unknown> | undefined;
      if (row && typeof row === 'object') s.quests[d.id] = { progress: intIn(row.progress, 0, dailyQuestGoal(d).count, 0), claimed: row.claimed === true };
    }
  }
  const dg = r.dungeon as Record<string, unknown> | undefined;
  for (const k of DUNGEON_KINDS) s.dungeon[k] = intIn(dg ? dg[k] : 0, 0, dungeonPerDay(k, s.day), 0);
  s.weekly = freshWeekly(s.day);
  const wk = r.weekly as Record<string, unknown> | undefined;
  if (wk && typeof wk === 'object' && intIn(wk.week, -1, 1e6, -2) === s.weekly.week) {
    const gr = (wk.goals && typeof wk.goals === 'object' ? wk.goals : {}) as Record<string, unknown>;
    for (const w of LIVE_WEEKS) for (const goal of w.goals) {
      const key = liveGoalKey(w, goal), row = gr[key] as Record<string, unknown> | undefined;
      if (row && typeof row === 'object') s.weekly.goals[key] = { progress: intIn(row.progress, 0, goal.count, 0), claimed: row.claimed === true };
    }
    s.weekly.finale = wk.finale === true;
    if (Array.isArray(wk.bounty)) s.weekly.bounty = wk.bounty.filter((x): x is string => typeof x === 'string' && x.length > 0 && x.length <= 60).slice(-WEEKLY_BOUNTY_SEEN_CAP);
  }
  s.inRun = r.inRun === 'vault' || r.inRun === 'mine' ? r.inRun : null;
  const t = r.tower as Record<string, unknown> | undefined;
  s.tower = { best: intIn(t?.best, 0, TOWER_MAX, 0), startedDay: intIn(t?.startedDay, -1, 1e7, -1), active: t?.active === true, floor: intIn(t?.floor, 0, TOWER_MAX, 0) };
  const g = r.granted as Record<string, unknown> | undefined;
  s.granted = { gold: intIn(g?.gold, 0, 1e12, 0), stones: intIn(g?.stones, 0, 1e9, 0), gems: intIn(g?.gems, 0, 1e9, 0), protect: intIn(g?.protect, 0, 1e6, 0) };
  s.log = Array.isArray(r.log) ? r.log.map(parseEntry).filter((e): e is LogEntry => e !== null).slice(-DAILY_APPLIED_CAP) : [];
  s.owed = [];
  if (Array.isArray(r.owed)) {
    for (const raw of r.owed) {
      const p = parsePayout(raw);
      if (p && !s.owed.some((x) => x.id === p.id)) s.owed.push(p);
    }
  } else if (r.v === 1 && (s.granted.gold > 0 || s.granted.stones > 0 || s.granted.protect > 0)) {
    // v1 빌드는 의뢰 수령을 장부에만 적고 지갑에 넣지 않았다(L5 실입력 확인): 그때까지의 누적분 전부가 미지급이다. 고정 id라 두 탭·재접속에도 한 번
    s.owed.push({ id: LEGACY_PAYOUT_ID, at: s.seenMs, gold: s.granted.gold, stones: s.granted.stones, tier: 0, protect: s.granted.protect, town: 1, paid: false });
  }
  trimOwed(s.owed);
  return s;
}

/** 저장 원문이 이 빌드보다 새 버전인지(새 빌드·다른 탭이 쓴 것 — 덮어쓰지 않고 읽기 전용으로 둔다) */
export function isFutureDaily(raw: string): boolean {
  try { const o = JSON.parse(raw) as { v?: unknown }; return !!o && typeof o === 'object' && typeof o.v === 'number' && o.v > DAILY_VERSION; } catch { return false; }
}
